/**
 * CellX General Product Quiz — Config for CellX Quiz Framework
 * ─────────────────────────────────────────────────────────────
 * 8-question product recommendation quiz.
 * Signal priority: goal → sleep → activity → conditions → age → gender → preference cap
 *
 * To create a new quiz, copy this pattern:
 *   1. Create assets/quiz-{name}.js
 *   2. Define your steps + recommend function
 *   3. Call CellXQuiz.register({ id: '{name}', ... })
 *   4. Load the script on the page
 *   5. Open with CellXQuiz.open('{name}')
 * ─────────────────────────────────────────────────────────────
 */
(function() {
  'use strict';

  // ── PRODUCT HANDLE RESOLVER ─────────────────────────────
  // Engine uses short keys internally. Some Shopify handles differ.
  var HANDLE_MAP = {
    'elara':    'myo-inositol-d-chiro-inositol-elara-suplemento-celular-femenino',
    'creatina': 'creatina-ultra-pura'
  };

  function toHandle(key) {
    return HANDLE_MAP[key] || key;
  }

  // ── GOAL → BASE STACK ──────────────────────────────────
  var GOAL_MAP = {
    'energia': {
      primary:   'powerup',
      secondary: ['espermidina', 'coq10', 'magnesioglicinato', 'nmn'],
      headline:  'Tu protocolo de energ\u00eda sostenida',
      subtext:   'Combinamos energ\u00eda celular profunda con protecci\u00f3n mitocondrial para que rindas sin depender de cafe\u00edna.'
    },
    'longevidad': {
      primary:   'nmn',
      secondary: ['espermidina', 'resveratrol', 'fisetina', 'ergotioneina'],
      headline:  'Tu protocolo de longevidad',
      subtext:   'Un stack dise\u00f1ado para activar NAD+, limpiar c\u00e9lulas senescentes y proteger tu ADN a largo plazo.'
    },
    'rendimiento': {
      primary:   'creatina',
      secondary: ['post-workout', 'powerup', 'coq10', 'magnesioglicinato'],
      headline:  'Tu protocolo de rendimiento f\u00edsico',
      subtext:   'Fuerza, recuperaci\u00f3n y energ\u00eda muscular: el stack para atletas que entrenan en serio.'
    },
    'sueno': {
      primary:   'magnesioglicinato',
      secondary: ['espermidina', 'nmn', 'ergotioneina'],
      headline:  'Tu protocolo de sue\u00f1o y recuperaci\u00f3n',
      subtext:   'El sue\u00f1o profundo es el fundamento de todo. Empezamos por ah\u00ed.'
    },
    'peso': {
      primary:   'glucotrim',
      secondary: ['glucoshield', 'creatina', 'coq10'],
      headline:  'Tu protocolo metab\u00f3lico',
      subtext:   'Control de glucosa, metabolismo activo y composici\u00f3n corporal \u2014 sin extremos.'
    },
    'piel': {
      primary:   'glasskin',
      secondary: ['keradence', 'nmn', 'vitaage'],
      headline:  'Tu protocolo de belleza celular',
      subtext:   'Col\u00e1geno, antioxidantes y longevidad celular para una piel que refleja salud desde adentro.'
    },
    'antiinflamatorio': {
      primary:   'quercetina',
      secondary: ['fisetina', 'ergotioneina', 'magnesioglicinato', 'nmn'],
      headline:  'Tu protocolo antiinflamatorio',
      subtext:   'Inflamaci\u00f3n cr\u00f3nica baja silenciosa. Este stack la ataca desde ra\u00edz.'
    },
    'cerebro': {
      primary:   'ergotioneina',
      secondary: ['powerup', 'nmn', 'coq10', 'magnesioglicinato'],
      headline:  'Tu protocolo de funci\u00f3n cognitiva',
      subtext:   'Foco, claridad mental y neuroprotecci\u00f3n a largo plazo en un solo stack.'
    }
  };

  // ── CONDITION OVERRIDES ────────────────────────────────
  var CONDITIONS = {
    'diabetes':       { push: ['glucoshield', 'glucotrim'], block: [] },
    'colesterol':     { push: ['coq10', 'resveratrol'],     block: [] },
    'stress':         { push: ['magnesioglicinato'],         block: [] },
    'inmunidad':      { push: ['quercetina', 'ergotioneina'], block: [] },
    'piel_reseca':    { push: ['glasskin', 'keradence'],     block: [] },
    'cancer_history': { push: [],  block: ['nmn', 'resveratrol', 'espermidina'] }
  };

  // ── HEADLINE VARIANTS ──────────────────────────────────
  var HEADLINES = {
    'sueno_poor':      'Tu mayor prioridad: restaurar el sue\u00f1o',
    'diabetes':        'Tu protocolo metab\u00f3lico personalizado',
    'cancer_history':  'Un protocolo conservador adaptado a tu historial'
  };

  // ── RECOMMENDATION ENGINE ──────────────────────────────

  function recommend(answers) {
    var goal  = GOAL_MAP[answers.goal] || GOAL_MAP['longevidad'];
    var limit = { 'todo_en_uno': 1, 'combinar': 2, 'plan_completo': 5 }[answers.preference] || 3;

    // Build ordered candidate queue: [primary, ...secondary]
    var queue = [goal.primary].concat(goal.secondary);

    // Sleep modifier: poor sleep → magnesio gets top-secondary slot
    var sleepPoor = answers.sleep === 'mal' || answers.sleep === 'bien_sin_reparacion';
    if (sleepPoor) {
      queue = _remove(queue, 'magnesioglicinato');
      queue.splice(1, 0, 'magnesioglicinato');
    }

    // Activity modifier: intense → creatina + post-workout bubble up
    if (answers.activity === 'intensa') {
      if (queue.indexOf('post-workout') === -1) queue.splice(1, 0, 'post-workout');
      if (queue.indexOf('creatina') === -1)     queue.splice(1, 0, 'creatina');
    }

    // Age modifier: 40+ → longevity tier gets appended if not present
    var ageNum = parseInt((answers.age || '30-39').split('-')[0], 10);
    if (ageNum >= 40) {
      ['fisetina', 'nmn', 'coq10'].forEach(function(h) {
        if (queue.indexOf(h) === -1) queue.push(h);
      });
    }

    // Condition overrides: push products forward, build block set
    var blocked = {};
    var conditions = (answers.conditions || []).filter(function(c) { return c !== 'ninguna'; });
    conditions.forEach(function(c) {
      var cfg = CONDITIONS[c];
      if (!cfg) return;
      cfg.block.forEach(function(h) { blocked[h] = true; });
      // Push in reverse so first item in push array gets highest priority
      cfg.push.slice().reverse().forEach(function(h) {
        if (queue.indexOf(h) === -1) queue.splice(1, 0, h);
      });
    });

    // Drop blocked products
    queue = queue.filter(function(h) { return !blocked[h]; });

    // Gender gate: Elara is women-only (safety net — not in any goal map)
    if (answers.gender !== 'mujer') {
      queue = queue.filter(function(h) { return h !== 'elara'; });
    }

    // Deduplicate (preserve first occurrence)
    var seen = {};
    queue = queue.filter(function(h) {
      if (seen[h]) return false;
      seen[h] = true;
      return true;
    });

    // Cap by preference, resolve to Shopify handles
    var products = queue.slice(0, limit).map(toHandle);

    // Headline selection
    var headline = goal.headline;
    var subtext  = goal.subtext;
    if (conditions.indexOf('cancer_history') !== -1) {
      headline = HEADLINES['cancer_history'];
      subtext  = 'Productos seguros y bien tolerados, sin activadores de proliferaci\u00f3n celular.';
    } else if (sleepPoor && answers.goal === 'sueno') {
      headline = HEADLINES['sueno_poor'];
    } else if (conditions.indexOf('diabetes') !== -1) {
      headline = HEADLINES['diabetes'];
    }

    // Build insight text
    var insight = _buildInsight(answers);

    return { products: products, headline: headline, subtext: subtext, insight: insight };
  }

  function _buildInsight(answers) {
    var lines = [];
    var sleepPoor = answers.sleep === 'mal' || answers.sleep === 'bien_sin_reparacion';
    if (sleepPoor) lines.push('Priorizamos recuperaci\u00f3n nocturna porque sin sue\u00f1o reparador ning\u00fan otro suplemento rinde al 100%.');
    if (answers.activity === 'intensa') lines.push('Tu nivel de actividad intensa eleva los requerimientos de recuperaci\u00f3n muscular y energ\u00eda mitocondrial.');
    var ageNum = parseInt((answers.age || '30-39').split('-')[0], 10);
    if (ageNum >= 40) lines.push('A partir de los 40, NAD+ y sen\u00f3lisis celular marcan la diferencia en c\u00f3mo envejeces.');
    if (!lines.length) lines.push('Este stack est\u00e1 calibrado para tu objetivo principal y tu estilo de vida actual.');
    return lines.join(' ');
  }

  function _remove(arr, item) {
    return arr.filter(function(h) { return h !== item; });
  }

  // ── REGISTER WITH FRAMEWORK ────────────────────────────

  CellXQuiz.register({
    id: 'general',

    intro: {
      headline: 'Encuentra tu protocolo ideal',
      subtext:  'Responde 8 preguntas r\u00e1pidas y te recomendaremos los suplementos exactos para tus objetivos.',
      cta:      'Comenzar'
    },

    steps: [
      {
        id: 'goal',
        question: '\u00bfCu\u00e1l es tu objetivo principal de salud?',
        type: 'single',
        options: [
          { key: 'energia',          label: 'M\u00e1s energ\u00eda y enfoque',           icon: '\u26a1' },
          { key: 'longevidad',       label: 'Longevidad y antienvejecimiento',  icon: '\ud83e\uddec' },
          { key: 'rendimiento',      label: 'Rendimiento f\u00edsico',                  icon: '\ud83d\udcaa' },
          { key: 'sueno',            label: 'Mejor sue\u00f1o',                         icon: '\ud83d\ude34' },
          { key: 'peso',             label: 'Control de peso',                 icon: '\u2696\ufe0f' },
          { key: 'piel',             label: 'Piel y apariencia',               icon: '\u2728' },
          { key: 'antiinflamatorio', label: 'Reducir inflamaci\u00f3n',                 icon: '\ud83d\udd25' },
          { key: 'cerebro',          label: 'Funci\u00f3n cognitiva',                   icon: '\ud83e\udde0' }
        ]
      },
      {
        id: 'age',
        question: '\u00bfCu\u00e1ntos a\u00f1os tienes?',
        type: 'single',
        options: [
          { key: '18-29', label: '18\u201329' },
          { key: '30-39', label: '30\u201339' },
          { key: '40-49', label: '40\u201349' },
          { key: '50-59', label: '50\u201359' },
          { key: '60+',   label: '60+' }
        ]
      },
      {
        id: 'gender',
        question: '\u00bfCon qu\u00e9 g\u00e9nero te identificas?',
        type: 'single',
        options: [
          { key: 'hombre', label: 'Hombre', icon: '\u2642\ufe0f' },
          { key: 'mujer',  label: 'Mujer',  icon: '\u2640\ufe0f' }
        ]
      },
      {
        id: 'sleep',
        question: '\u00bfC\u00f3mo es la calidad de tu sue\u00f1o?',
        type: 'single',
        options: [
          { key: 'bien',                label: 'Duermo bien y descanso',  icon: '\ud83d\ude0a' },
          { key: 'bien_sin_reparacion', label: 'Duermo pero no descanso', icon: '\ud83d\ude10' },
          { key: 'mal',                 label: 'Duermo mal o muy poco',   icon: '\ud83d\ude29' }
        ]
      },
      {
        id: 'activity',
        question: '\u00bfCu\u00e1l es tu nivel de actividad f\u00edsica?',
        type: 'single',
        options: [
          { key: 'sedentaria', label: 'Sedentaria',          icon: '\ud83e\ude91' },
          { key: 'moderada',   label: 'Moderada (2\u20133x/sem)',  icon: '\ud83d\udeb6' },
          { key: 'activa',     label: 'Activa (4\u20135x/sem)',    icon: '\ud83c\udfc3' },
          { key: 'intensa',    label: 'Intensa (6+x/sem)',   icon: '\ud83c\udfcb\ufe0f' }
        ]
      },
      {
        id: 'diet',
        question: '\u00bfC\u00f3mo describir\u00edas tu alimentaci\u00f3n?',
        type: 'single',
        options: [
          { key: 'excelente',  label: 'Excelente \u2014 muy cuidada',      icon: '\ud83e\udd57' },
          { key: 'balanceada', label: 'Balanceada \u2014 como bien',       icon: '\ud83c\udf7d\ufe0f' },
          { key: 'irregular',  label: 'Irregular \u2014 depende del d\u00eda', icon: '\ud83c\udf55' },
          { key: 'deficiente', label: 'Deficiente \u2014 como mal',       icon: '\ud83c\udf54' }
        ]
      },
      {
        id: 'preference',
        question: '\u00bfCu\u00e1ntos suplementos est\u00e1s dispuesto/a a tomar?',
        type: 'single',
        options: [
          { key: 'todo_en_uno',   label: 'Solo uno \u2014 lo esencial',   icon: '1\ufe0f\u20e3' },
          { key: 'combinar',      label: 'Dos \u2014 combinaci\u00f3n b\u00e1sica', icon: '2\ufe0f\u20e3' },
          { key: 'plan_completo', label: 'Un plan completo',       icon: '\ud83d\udccb' }
        ]
      },
      {
        id: 'conditions',
        question: '\u00bfTienes alguna de estas condiciones?',
        type: 'multi',
        options: [
          { key: 'diabetes',       label: 'Diabetes o resistencia a insulina', icon: '\ud83d\udc89' },
          { key: 'colesterol',     label: 'Colesterol alto',                   icon: '\u2764\ufe0f' },
          { key: 'stress',         label: 'Estr\u00e9s cr\u00f3nico o ansiedad',              icon: '\ud83d\ude30' },
          { key: 'inmunidad',      label: 'Sistema inmune d\u00e9bil',                 icon: '\ud83d\udee1\ufe0f' },
          { key: 'piel_reseca',    label: 'Piel reseca / l\u00edneas finas',          icon: '\ud83e\uddf4' },
          { key: 'cancer_history', label: 'Historial de c\u00e1ncer',                 icon: '\ud83c\udfe5' },
          { key: 'ninguna',        label: 'Ninguna de las anteriores',         icon: '\u2705' }
        ]
      }
    ],

    email: {
      enabled: true,
      headline: 'Recibe tu protocolo personalizado',
      subtext:  'Te enviaremos tu recomendaci\u00f3n por email con informaci\u00f3n detallada de cada producto.',
      placeholder: 'tu@email.com',
      cta: 'Ver mis resultados',
      skip_label: 'Omitir',
      klaviyo_list: null,
      klaviyo_event: 'General Quiz Completed'
    },

    recommend: recommend,

    results: {
      cta_single: 'Agregar al carrito',
      cta_all: 'Agregar todo al carrito',
      retake_label: 'Volver a empezar'
    }
  });

})();
